import { Table, TableHeaderRow, TableHeaderCell, TableRow, TableCell, Button, Input, FlexBox, Title } from '@ui5/webcomponents-react';
import type { CollectionSection, MetaField } from '@b1links/core';
import type { EntityMeta, Rec } from '../api/client';
import { findField, formatValue, labelFor } from './format';
import { LookupInput } from './LookupInput';

interface Props { section: CollectionSection; meta?: EntityMeta; rows: Rec[]; mode: 'display' | 'edit' | 'create'; currency?: string; onChange(rows: Rec[]): void }

/** Child-collection grid (DocumentLines, BPAddresses, ItemPrices…) with inline editing on designer-chosen columns. */
export function CollectionTable({ section, meta, rows, mode, currency, onChange }: Props) {
  const ct = meta?.fields.find(f => f.name === section.collection)?.complexType;
  const mfOf = (field: string): MetaField | undefined => findField(meta, field, ct);
  const editing = mode !== 'display';
  const canEdit = (f: string) => editing && (section.editable ?? []).includes(f);
  const setCell = (i: number, f: string, v: unknown) => onChange(rows.map((r, j) => (j === i ? { ...r, [f]: v } : r)));

  return (
    <div>
      {editing && section.addLookup && (
        <FlexBox alignItems="Center" style={{ gap: '0.5rem', marginBottom: '0.5rem', maxWidth: '32rem' }}>
          <LookupInput lookup={section.addLookup} value="" placeholder={`Add ${section.addLookup.entity}…`}
            onSelect={(row, key) => { if (!row) return; const nr: Rec = { ...(section.rowDefaults ?? {}), [section.addLookup!.key]: key };
              if (section.addLookup!.copy) for (const [t, s] of Object.entries(section.addLookup!.copy)) nr[t] = row[s]; onChange([...rows, nr]); }} />
        </FlexBox>
      )}
      <div style={{ overflowX: 'auto' }}>
        {/* Popin mode: on a phone each line folds into a label/value block instead of a cut-off grid. */}
        <Table overflowMode="Popin" headerRow={<TableHeaderRow>
          <TableHeaderCell width="3rem" minWidth="3rem" importance={3}><span>#</span></TableHeaderCell>
          {section.columns.map((c, i) => <TableHeaderCell key={c.field} width={c.width} minWidth={c.width ?? '8rem'} importance={i === 0 ? 2 : 0}><span>{labelFor(c.field, mfOf(c.field), c.label)}</span></TableHeaderCell>)}
          {editing && <TableHeaderCell width="3rem" minWidth="3rem" importance={3}><span></span></TableHeaderCell>}
        </TableHeaderRow>}>
          {rows.map((r, i) => (
            <TableRow key={i}>
              <TableCell><span>{i + 1}</span></TableCell>
              {section.columns.map(c => (
                <TableCell key={c.field}>
                  {canEdit(c.field)
                    ? <Input value={r[c.field] == null ? '' : String(r[c.field])} type={mfOf(c.field)?.kind === 'int' || mfOf(c.field)?.kind === 'decimal' ? 'Number' : 'Text'} style={{ width: '100%', minWidth: '5rem' }}
                        onChange={e => setCell(i, c.field, mfOf(c.field)?.kind === 'int' || mfOf(c.field)?.kind === 'decimal' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value)} />
                    : <span>{formatValue(r[c.field], c, mfOf(c.field), c.format === 'money' ? currency : undefined)}</span>}
                </TableCell>
              ))}
              {editing && <TableCell><Button icon="delete" design="Transparent" onClick={() => onChange(rows.filter((_, j) => j !== i))} /></TableCell>}
            </TableRow>
          ))}
        </Table>
      </div>
      {rows.length === 0 && <Title level="H6" style={{ margin: '0.5rem 0', color: 'var(--sapNeutralTextColor)' }}>No rows</Title>}
    </div>
  );
}
